import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, CircularProgress,
  FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, Slider, Tab, Tabs,
  TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { adminApi } from '../../api';
import ToolLayoutEditorPanel from '../ToolLayoutEditorPanel';
import ShortcutCaptureField from '../ShortcutCaptureField';
import { MIXED_VIEWPORT_LAYOUT_PRESETS, normalizeViewportLayout } from '../../viewportLayoutGeometry';
import {
  DEFAULT_TOOL_LAYOUT,
  TOOL_LAYOUT_PREF_NAMESPACE,
  normalizeToolLayoutPreferences,
} from '../../viewerToolLayoutPreferences';
import {
  DEFAULT_TOOL_SHORTCUTS,
  KEYBOARD_SHORTCUT_PREF_NAMESPACE,
  TOOL_SHORTCUT_LABELS,
  normalizeKeyboardShortcutPreferences,
} from '../viewerKeyboardShortcuts';
import {
  DEFAULT_WORKSPACE_PREFS,
  TOOL_COMPLETION_BEHAVIORS,
  WORKSPACE_PREF_NAMESPACE,
  WORKSPACE_PRESETS,
  applyWorkspacePreset,
  normalizeWorkspacePrefs,
} from '../../viewerWorkspacePreferences';
import {
  DEFAULT_ANNOTATION_APPEARANCE,
  ANNOTATION_APPEARANCE_PREF_NAMESPACE,
  normalizeAnnotationAppearance,
} from '../../annotationAppearance';
import {
  DEFAULT_OVERLAY_CONFIG,
  OVERLAY_FONT_SIZE_MAX,
  OVERLAY_FONT_SIZE_MIN,
  normalizeOverlayConfig,
} from '../../overlayConfig';

const PROFILE_EDITOR_MARKER = 'KAIROS_ADMIN_PROFILE_EDITOR';
const CODELESS_PROFILE_CONTROLS_MARKER = 'KAIROS_CODELESS_PROFILE_CONTROLS';
const PROFILE_USER_SEARCH_MARKER = 'KAIROS_PROFILE_USER_SEARCH';
const MULTI_HANGING_PROTOCOLS_MARKER = 'KAIROS_MULTI_HANGING_PROTOCOLS';
const PROFILE_EDITOR_NAMESPACE_MARKERS = ['viewer.tool_layout', 'viewer.hanging_protocols', 'viewer.keyboard_shortcuts'];
const HANGING_PROTOCOL_MODALITIES = ['CT', 'MR', 'PT', 'PET', 'US', 'MG', 'DX', 'CR'];

const layoutFromKey = (key = '2x2') => {
  if (MIXED_VIEWPORT_LAYOUT_PRESETS[key]) return normalizeViewportLayout(MIXED_VIEWPORT_LAYOUT_PRESETS[key]);
  const [rows, cols] = String(key).split('x').map(part => Math.max(1, Number(part) || 1));
  return normalizeViewportLayout({ rows: rows || 2, cols: cols || 2 });
};
const layoutToKey = (layout) => {
  if (typeof layout === 'string') return layout;
  if (layout?.presetId && MIXED_VIEWPORT_LAYOUT_PRESETS[layout.presetId]) return layout.presetId;
  const rows = Math.max(1, Number(layout?.rows || 2));
  const cols = Math.max(1, Number(layout?.cols || 2));
  return `${rows}x${cols}`;
};
const makeDefaultHangingProtocol = (modality = 'CT', index = 0) => ({
  id: `admin-${String(modality).toLowerCase()}-default-${index + 1}`,
  name: `${modality} default reading protocol`,
  modality,
  matchingRules: { modalities: [modality] },
  layout: layoutFromKey('2x2'),
  viewports: [],
  priority: 50,
  autoApply: true,
  defaultForModality: true,
});
const normalizeHangingProtocols = (value) => {
  const source = Array.isArray(value) && value.length ? value : HANGING_PROTOCOL_MODALITIES.map(makeDefaultHangingProtocol);
  const seenDefaults = new Set();
  const normalized = source.map((protocol, index) => {
    const sourceProtocol = protocol && typeof protocol === 'object' && !Array.isArray(protocol) ? protocol : {};
    const modality = String(sourceProtocol.modality || sourceProtocol.matchingRules?.modalities?.[0] || 'CT').trim().toUpperCase();
    const wantsDefault = sourceProtocol.defaultForModality === true || sourceProtocol.isDefault === true || sourceProtocol.default === true;
    const defaultForModality = wantsDefault && !seenDefaults.has(modality);
    if (defaultForModality) seenDefaults.add(modality);
    return {
      ...sourceProtocol,
      id: sourceProtocol.id || `admin-${modality.toLowerCase()}-${index + 1}`,
      name: sourceProtocol.name || `${modality} reading protocol`,
      modality,
      matchingRules: { ...(sourceProtocol.matchingRules || {}), modalities: [modality] },
      layout: normalizeViewportLayout(sourceProtocol.layout || layoutFromKey('2x2')),
      viewports: Array.isArray(sourceProtocol.viewports) ? sourceProtocol.viewports : [],
      priority: Number(sourceProtocol.priority ?? 50),
      autoApply: sourceProtocol.autoApply !== false,
      defaultForModality,
    };
  });
  const defaulted = new Set(normalized.filter(protocol => protocol.defaultForModality).map(protocol => protocol.modality));
  return normalized.map(protocol => {
    if (!defaulted.has(protocol.modality)) {
      defaulted.add(protocol.modality);
      return { ...protocol, defaultForModality: true };
    }
    return protocol;
  });
};

export const EDITABLE_PROFILE_NAMESPACES = [
  { namespace: TOOL_LAYOUT_PREF_NAMESPACE, label: 'Tool layout', normalize: normalizeToolLayoutPreferences },
  { namespace: 'viewer.hanging_protocols', label: 'Hanging protocols', normalize: normalizeHangingProtocols },
  { namespace: KEYBOARD_SHORTCUT_PREF_NAMESPACE, label: 'Keyboard shortcuts', normalize: normalizeKeyboardShortcutPreferences },
  { namespace: WORKSPACE_PREF_NAMESPACE, label: 'Workspace chrome', normalize: normalizeWorkspacePrefs },
  { namespace: ANNOTATION_APPEARANCE_PREF_NAMESPACE, label: 'Annotation appearance', normalize: normalizeAnnotationAppearance },
  { namespace: 'viewer.overlay', label: 'Overlay', normalize: normalizeOverlayConfig },
];

const pretty = (value) => JSON.stringify(value ?? {}, null, 2);
const parseJson = (value) => JSON.parse(value || 'null');
const safeUserLabel = (user) => `${user.username || user.id}${user.is_active === false ? ' (disabled)' : ''}`;
const changedPaths = (value, baseline, prefix = '') => {
  if (pretty(value) === pretty(baseline)) return [];
  if (!value || !baseline || typeof value !== 'object' || typeof baseline !== 'object' || Array.isArray(value) || Array.isArray(baseline)) return [prefix || 'value'];
  return Array.from(new Set([...Object.keys(value), ...Object.keys(baseline)]))
    .flatMap(key => changedPaths(value[key], baseline[key], prefix ? `${prefix}.${key}` : key));
};

const useDraftValue = (draft, selectedSpec) => {
  try {
    return selectedSpec.normalize(parseJson(draft));
  } catch (_) {
    return selectedSpec.normalize(undefined);
  }
};

export default function ProfileEditor() {
  const [users, setUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchOffset, setUserSearchOffset] = useState(0);
  const [userSearchHasMore, setUserSearchHasMore] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedNamespace, setSelectedNamespace] = useState(EDITABLE_PROFILE_NAMESPACES[0].namespace);
  const [preferences, setPreferences] = useState({});
  const [draft, setDraft] = useState('{}');
  const [jsonExpanded, setJsonExpanded] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPrefs, setLoadingPrefs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showDifferences, setShowDifferences] = useState(true);
  const [copySourceUserId, setCopySourceUserId] = useState('');

  const selectedSpec = useMemo(
    () => EDITABLE_PROFILE_NAMESPACES.find(item => item.namespace === selectedNamespace) || EDITABLE_PROFILE_NAMESPACES[0],
    [selectedNamespace]
  );
  const selectedUser = users.find(user => String(user.id) === String(selectedUserId));
  const currentValue = preferences[selectedNamespace]?.value;
  const draftValue = useDraftValue(draft, selectedSpec);
  const dirty = Boolean(selectedUserId) && draft !== pretty(currentValue ?? selectedSpec.normalize(undefined));
  const defaultValue = selectedSpec.normalize(undefined);
  const differencePaths = changedPaths(draftValue, defaultValue);
  const latestProfileChange = Object.values(preferences).map(row => row?.updated_at || row?.created_at).filter(Boolean).sort().pop();

  const chooseUser = (userId) => {
    if (dirty && typeof window !== 'undefined' && !window.confirm('Discard unsaved profile changes and switch users?')) return;
    setSelectedUserId(String(userId));
  };
  const chooseNamespace = (namespace) => {
    if (dirty && typeof window !== 'undefined' && !window.confirm('Discard unsaved changes and switch profile sections?')) return;
    setSelectedNamespace(namespace);
  };

  const setNormalizedDraft = useCallback((value) => {
    setDraft(pretty(selectedSpec.normalize(value)));
  }, [selectedSpec]);

  const searchProfileUsers = useCallback(async (query = userSearchQuery, offset = 0, append = false) => {
    setLoadingUsers(true);
    setError('');
    try {
      const res = await adminApi.searchProfileUsers(query, 20, offset);
      const safeUsers = (res.users || []).map(user => ({
        id: user.id,
        username: user.username,
        is_active: user.is_active,
      }));
      setUsers(prev => append ? [...prev, ...safeUsers.filter(user => !prev.some(existing => String(existing.id) === String(user.id)))] : safeUsers);
      setUserSearchHasMore(Boolean(res.has_more));
      setUserSearchOffset(offset);
      if (!selectedUserId && safeUsers.length) setSelectedUserId(String(safeUsers[0].id));
    } catch (e) {
      setUsers([]);
      setUserSearchHasMore(false);
      setError(e.response?.data?.detail || 'Unable to search safe user list for Profile Editor.');
    } finally {
      setLoadingUsers(false);
    }
  }, [selectedUserId, userSearchQuery]);

  const loadPreferences = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingPrefs(true);
    setError('');
    setNotice('');
    try {
      const data = await adminApi.listProfilePreferences(userId);
      const byNamespace = Object.fromEntries((data.preferences || []).map(item => [item.namespace, item]));
      setPreferences(byNamespace);
      const value = byNamespace[selectedNamespace]?.value ?? selectedSpec.normalize(undefined);
      setDraft(pretty(value));
    } catch (e) {
      setPreferences({});
      setDraft('{}');
      setError(e.response?.status === 401 || e.response?.status === 403
        ? 'You do not currently have permission to view or edit this profile.'
        : (e.response?.data?.detail || 'Unable to load profile preferences.'));
    } finally {
      setLoadingPrefs(false);
    }
  }, [selectedNamespace, selectedSpec]);

  useEffect(() => {
    const timer = setTimeout(() => searchProfileUsers(userSearchQuery, 0, false), 250);
    return () => clearTimeout(timer);
  }, [userSearchQuery, searchProfileUsers]);
  useEffect(() => { if (selectedUserId) loadPreferences(selectedUserId); }, [selectedUserId, loadPreferences]);
  useEffect(() => {
    const value = preferences[selectedNamespace]?.value ?? selectedSpec.normalize(undefined);
    setDraft(pretty(value));
    setJsonExpanded(false);
  }, [preferences, selectedNamespace, selectedSpec]);

  const save = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const parsed = parseJson(draft);
      const normalized = selectedSpec.normalize(parsed);
      const row = await adminApi.saveProfilePreference(selectedUserId, selectedNamespace, normalized);
      setPreferences(prev => ({ ...prev, [selectedNamespace]: row }));
      setDraft(pretty(row.value));
      setNotice(`Saved ${selectedNamespace} for ${selectedUser?.username || selectedUserId}.`);
    } catch (e) {
      setError(e instanceof SyntaxError ? 'Validation summary: JSON is malformed.' : (e.response?.data?.detail || 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await adminApi.resetProfilePreference(selectedUserId, selectedNamespace);
      setPreferences(prev => {
        const next = { ...prev };
        delete next[selectedNamespace];
        return next;
      });
      setDraft(pretty(selectedSpec.normalize(undefined)));
      setNotice(`Reset ${selectedNamespace} for ${selectedUser?.username || selectedUserId}.`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed.');
    } finally {
      setSaving(false);
    }
  };

  const copyJsonToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setNotice('Copied JSON for export.');
    } catch (_) {
      setNotice('JSON is visible for manual copy/export.');
    }
  };

  const copyNamespaceFromUser = async () => {
    if (!copySourceUserId || String(copySourceUserId) === String(selectedUserId)) return;
    setSaving(true); setError(''); setNotice('');
    try {
      const data = await adminApi.listProfilePreferences(copySourceUserId);
      const row = (data.preferences || []).find(item => item.namespace === selectedNamespace);
      setNormalizedDraft(row?.value ?? selectedSpec.normalize(undefined));
      const source = users.find(user => String(user.id) === String(copySourceUserId));
      setNotice(`Copied ${selectedSpec.label} from ${source?.username || copySourceUserId} into this draft. Review and save to apply.`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to copy profile settings from that user.');
    } finally {
      setSaving(false);
    }
  };

  const applyProfileTemplate = (templateId) => {
    if (!templateId) return;
    if (selectedNamespace === WORKSPACE_PREF_NAMESPACE) {
      const preset = WORKSPACE_PRESETS.find(item => item.id === templateId);
      setNormalizedDraft(preset ? applyWorkspacePreset(preset.id, DEFAULT_WORKSPACE_PREFS) : DEFAULT_WORKSPACE_PREFS);
    } else if (templateId === 'baseline') {
      setNormalizedDraft(selectedSpec.normalize(undefined));
    }
    setNotice('Template loaded into the draft. Review differences and save to apply.');
  };

  const updateShortcutDraft = (toolId, key) => {
    const base = normalizeKeyboardShortcutPreferences(draftValue);
    setNormalizedDraft({ toolShortcuts: { ...base.toolShortcuts, [toolId]: key } });
  };
  const updateWorkspaceDraft = (patch) => {
    setNormalizedDraft({ ...normalizeWorkspacePrefs(draftValue || DEFAULT_WORKSPACE_PREFS), ...patch });
  };
  const updateAnnotationDraft = (patch) => {
    setNormalizedDraft({ ...normalizeAnnotationAppearance(draftValue || DEFAULT_ANNOTATION_APPEARANCE), ...patch });
  };
  const updateOverlayDraft = (patch) => {
    setNormalizedDraft({ ...normalizeOverlayConfig(draftValue || DEFAULT_OVERLAY_CONFIG), ...patch });
  };
  const updateHangingProtocolDraft = (index, patch) => {
    const protocols = normalizeHangingProtocols(draftValue);
    setNormalizedDraft(protocols.map((protocol, idx) => {
      if (idx !== index) return protocol;
      const next = { ...protocol, ...patch };
      if (patch.modality) next.matchingRules = { ...(next.matchingRules || {}), modalities: [patch.modality] };
      if (patch.layout) next.layout = layoutFromKey(patch.layout);
      return next;
    }));
  };
  const addHangingProtocolDraft = () => {
    const protocols = normalizeHangingProtocols(draftValue);
    const modality = HANGING_PROTOCOL_MODALITIES.find(item => !protocols.some(protocol => protocol.modality === item)) || 'CT';
    setNormalizedDraft([...protocols, { ...makeDefaultHangingProtocol(modality, protocols.length), defaultForModality: !protocols.some(protocol => protocol.modality === modality) }]);
  };
  const removeHangingProtocolDraft = (index) => {
    const protocols = normalizeHangingProtocols(draftValue);
    if (protocols.length <= 1) return;
    if (typeof window !== 'undefined' && !window.confirm(`Remove ${protocols[index]?.name || 'this hanging protocol'} from the draft?`)) return;
    setNormalizedDraft(protocols.filter((_, idx) => idx !== index));
  };
  const setDefaultHangingProtocolForModality = (index) => {
    const protocols = normalizeHangingProtocols(draftValue);
    const modality = protocols[index]?.modality || 'CT';
    setNormalizedDraft(protocols.map((protocol, idx) => ({
      ...protocol,
      defaultForModality: protocol.modality === modality ? idx === index : protocol.defaultForModality === true,
    })));
  };

  const renderToolLayoutControls = () => {
    const value = normalizeToolLayoutPreferences(draftValue || DEFAULT_TOOL_LAYOUT);
    return <ToolLayoutEditorPanel value={value} onChange={setNormalizedDraft} />;
  };

  const renderShortcutControls = () => {
    const value = normalizeKeyboardShortcutPreferences(draftValue);
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 1 }}>
        {Object.keys(DEFAULT_TOOL_SHORTCUTS).map(toolId => (
          <ShortcutCaptureField key={toolId} label={TOOL_SHORTCUT_LABELS[toolId] || toolId} value={value.toolShortcuts[toolId] || ''} onChange={shortcut => updateShortcutDraft(toolId, shortcut)} />
        ))}
      </Box>
    );
  };

  const renderWorkspaceControls = () => {
    const value = normalizeWorkspacePrefs(draftValue || DEFAULT_WORKSPACE_PREFS);
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Workspace preset</InputLabel>
          <Select label="Workspace preset" value={value.workspacePreset} onChange={e => updateWorkspaceDraft({ workspacePreset: e.target.value })}>
            {WORKSPACE_PRESETS.map(preset => <MenuItem key={preset.id} value={preset.id}>{preset.label} — {preset.description}</MenuItem>)}
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel control={<Checkbox checked={value.seriesRailVisible} onChange={e => updateWorkspaceDraft({ seriesRailVisible: e.target.checked })} />} label="Show Series rail" />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Series panel position</InputLabel>
          <Select label="Series panel position" value={value.seriesRailPosition} onChange={e => updateWorkspaceDraft({ seriesRailPosition: e.target.value, workspacePreset: 'custom' })}>
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="top">Top</MenuItem>
            <MenuItem value="bottom">Bottom</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel control={<Checkbox checked={value.reportRailVisible} onChange={e => updateWorkspaceDraft({ reportRailVisible: e.target.checked })} />} label="Show Report rail" />
        <FormControlLabel control={<Checkbox checked={value.commandDeckCollapsed} onChange={e => updateWorkspaceDraft({ commandDeckCollapsed: e.target.checked })} />} label="Collapse top command deck" />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>After tool completes</InputLabel>
          <Select label="After tool completes" value={value.toolCompletionBehavior} onChange={e => updateWorkspaceDraft({ toolCompletionBehavior: e.target.value })}>
            <MenuItem value={TOOL_COMPLETION_BEHAVIORS.SCROLL}>Return to scroll</MenuItem>
            <MenuItem value={TOOL_COMPLETION_BEHAVIORS.STAY}>Stay on active tool</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  };

  const renderAnnotationControls = () => {
    const value = normalizeAnnotationAppearance(draftValue || DEFAULT_ANNOTATION_APPEARANCE);
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField size="small" type="color" label="Annotation color" value={value.color} onChange={e => updateAnnotationDraft({ color: e.target.value })} sx={{ minWidth: 160 }} />
        <TextField size="small" type="number" label="Line width" value={value.lineWidth} onChange={e => updateAnnotationDraft({ lineWidth: Number(e.target.value) })} inputProps={{ min: 1, max: 6 }} />
        <TextField size="small" type="number" label="Font size" value={value.fontSize} onChange={e => updateAnnotationDraft({ fontSize: Number(e.target.value) })} inputProps={{ min: 8, max: 24 }} />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Font family</InputLabel>
          <Select label="Font family" value={value.fontFamily} onChange={e => updateAnnotationDraft({ fontFamily: e.target.value })}>
            <MenuItem value="monospace">Monospace</MenuItem>
            <MenuItem value="sans">Sans</MenuItem>
            <MenuItem value="serif">Serif</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel control={<Checkbox checked={value.showLabels} onChange={e => updateAnnotationDraft({ showLabels: e.target.checked })} />} label="Show labels" />
      </Box>
    );
  };

  const renderHangingProtocolControls = () => {
    const protocols = normalizeHangingProtocols(draftValue);
    return (
      <Box data-kairos-marker={MULTI_HANGING_PROTOCOLS_MARKER}>
        <Typography sx={{ color: '#86a4af', fontSize: 12, mb: 1 }}>
          At least one default is required for each modality type. Use the default checkbox to choose exactly one default per modality.
        </Typography>
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          {protocols.map((protocol, index) => (
            <Paper key={protocol.id || index} sx={{ p: 1.25, bgcolor: '#091820', border: '1px solid rgba(110,239,255,0.12)' }}>
              <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" label="Protocol name" value={protocol.name || ''} onChange={e => updateHangingProtocolDraft(index, { name: e.target.value })} sx={{ minWidth: 260 }} />
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Modality</InputLabel>
                  <Select label="Modality" value={protocol.modality || 'CT'} onChange={e => updateHangingProtocolDraft(index, { modality: e.target.value })}>
                    {HANGING_PROTOCOL_MODALITIES.map(modality => <MenuItem key={modality} value={modality}>{modality}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Default layout</InputLabel>
                  <Select label="Default layout" value={layoutToKey(protocol.layout)} onChange={e => updateHangingProtocolDraft(index, { layout: e.target.value })}>
                    <MenuItem value="1x1">1×1</MenuItem>
                    <MenuItem value="1x2">1×2</MenuItem>
                    <MenuItem value="2x2">2×2</MenuItem>
                    <MenuItem value="3x3">3×3</MenuItem>
                    <MenuItem value="2-over-3">2 over 3 (5 viewports)</MenuItem>
                    <MenuItem value="2x2-plus-1x3">2×2 + 1×3 (7 viewports)</MenuItem>
                  </Select>
                </FormControl>
                <TextField size="small" type="number" label="Priority" value={protocol.priority ?? 50} onChange={e => updateHangingProtocolDraft(index, { priority: Number(e.target.value) })} inputProps={{ min: 0, max: 100 }} sx={{ width: 110 }} />
                <FormControlLabel control={<Checkbox checked={protocol.defaultForModality === true} onChange={() => setDefaultHangingProtocolForModality(index)} />} label="Default for modality" />
                <FormControlLabel control={<Checkbox checked={protocol.autoApply !== false} onChange={e => updateHangingProtocolDraft(index, { autoApply: e.target.checked })} />} label="Auto-apply" />
                <Button size="small" disabled={protocols.length <= 1} onClick={() => removeHangingProtocolDraft(index)}>Remove</Button>
              </Box>
            </Paper>
          ))}
        </Box>
        <Button size="small" onClick={addHangingProtocolDraft} sx={{ mt: 1 }}>Add protocol</Button>
      </Box>
    );
  };

  const renderOverlayControls = () => {
    const value = normalizeOverlayConfig(draftValue || DEFAULT_OVERLAY_CONFIG);
    return (
      <Box sx={{ display: 'grid', gap: 1.5, maxWidth: 620 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel control={<Checkbox checked={value.enabled} onChange={e => updateOverlayDraft({ enabled: e.target.checked })} />} label="Viewport attribute overlays" />
          <FormControlLabel control={<Checkbox checked={value.showPatientBanner} onChange={e => updateOverlayDraft({ showPatientBanner: e.target.checked })} />} label="Patient banner" />
          <FormControlLabel control={<Checkbox checked={value.showViewportLabels} onChange={e => updateOverlayDraft({ showViewportLabels: e.target.checked })} />} label="Viewport labels" />
          <FormControlLabel control={<Checkbox checked={value.showDicomAttributes} onChange={e => updateOverlayDraft({ showDicomAttributes: e.target.checked })} />} label="DICOM attributes" />
          <FormControlLabel control={<Checkbox checked={value.showOrientationMarkers} onChange={e => updateOverlayDraft({ showOrientationMarkers: e.target.checked })} />} label="Orientation markers" />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#9fb6bd', mb: 0.5 }}>Overlay attribute text size: {value.fontSize}px</Typography>
            <Slider aria-label="Overlay attribute text size" min={OVERLAY_FONT_SIZE_MIN} max={OVERLAY_FONT_SIZE_MAX} value={value.fontSize} onChange={(_, fontSize) => updateOverlayDraft({ fontSize })} valueLabelDisplay="auto" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#9fb6bd', mb: 0.5 }}>Measurement and ROI text size: {value.measurementFontSize}px</Typography>
            <Slider aria-label="Measurement and ROI text size" min={OVERLAY_FONT_SIZE_MIN} max={OVERLAY_FONT_SIZE_MAX} value={value.measurementFontSize} onChange={(_, measurementFontSize) => updateOverlayDraft({ measurementFontSize })} valueLabelDisplay="auto" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" type="color" label="Overlay text color" value={value.color} onChange={e => updateOverlayDraft({ color: e.target.value })} sx={{ width: 180 }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Overlay density</InputLabel>
            <Select label="Overlay density" value={value.density} onChange={e => updateOverlayDraft({ density: e.target.value })}>
              <MenuItem value="compact">Compact</MenuItem>
              <MenuItem value="comfortable">Comfortable</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Typography sx={{ fontSize: 11, color: '#607d8b' }}>Field placement remains editable from OVERLAY in the diagnostic viewer and is preserved here.</Typography>
      </Box>
    );
  };

  const renderCodeLessEditor = () => {
    if (selectedNamespace === TOOL_LAYOUT_PREF_NAMESPACE) return renderToolLayoutControls();
    if (selectedNamespace === KEYBOARD_SHORTCUT_PREF_NAMESPACE) return renderShortcutControls();
    if (selectedNamespace === WORKSPACE_PREF_NAMESPACE) return renderWorkspaceControls();
    if (selectedNamespace === ANNOTATION_APPEARANCE_PREF_NAMESPACE) return renderAnnotationControls();
    if (selectedNamespace === 'viewer.hanging_protocols') return renderHangingProtocolControls();
    if (selectedNamespace === 'viewer.overlay') return renderOverlayControls();
    return <Typography sx={{ color: '#8aa4ae' }}>This section is available in the export panel below.</Typography>;
  };

  if (loadingUsers) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}><CircularProgress size={24} /></Box>;
  }

  return (
    <Box data-kairos-marker={PROFILE_EDITOR_MARKER} data-profile-editor-namespaces={PROFILE_EDITOR_NAMESPACE_MARKERS.join(',')} sx={{ p: 3, color: '#d7fbff' }}>
      <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: 1, color: 'primary.main' }}>PROFILE EDITOR</Typography>
      <Typography sx={{ fontSize: 11, color: '#6d8792', mb: 2 }}>
        Manage viewer profile settings on behalf of any user. Changes are explicit, reviewable, and applied only after Save.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5, bgcolor: '#1a0707', color: '#ffd8d8' }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1.5, bgcolor: '#071a12', color: '#caffdf' }}>{notice}</Alert>}
      {dirty && <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="dirty-state-banner">Unsaved changes — save or reset before switching users.</Alert>}

      <Paper sx={{ p: 2, bgcolor: '#0d141b', border: '1px solid rgba(110,239,255,0.18)', color: '#d7fbff' }}>
        <Box data-kairos-marker={PROFILE_USER_SEARCH_MARKER} sx={{ display: 'grid', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            label="Search users by username or id"
            value={userSearchQuery}
            onChange={e => setUserSearchQuery(e.target.value)}
            inputProps={{ 'aria-label': 'Search users by username or id' }}
            sx={{ maxWidth: 420 }}
          />
          <Box role="listbox" aria-label="Profile users" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 0.65, alignItems: 'center', maxHeight: 220, overflow: 'auto' }}>
            {users.map(user => (
              <Button
                key={user.id}
                size="small"
                variant={String(user.id) === String(selectedUserId) ? 'contained' : 'outlined'}
                role="option"
                aria-selected={String(user.id) === String(selectedUserId)}
                onClick={() => chooseUser(user.id)}
                sx={{ textTransform: 'none', justifyContent: 'flex-start', px: 1.1, py: 0.7 }}
              >
                <Box component="span" sx={{ width: 7, height: 7, mr: 1, borderRadius: '50%', bgcolor: user.is_active === false ? '#6b7780' : '#7ee787' }} />{safeUserLabel(user)}
              </Button>
            ))}
            {loadingUsers && <CircularProgress size={18} />}
            {userSearchHasMore && (
              <Button size="small" onClick={() => searchProfileUsers(userSearchQuery, userSearchOffset + 20, true)}>Load more users</Button>
            )}
          </Box>
          <Box sx={{ color: '#7b95a1', fontSize: 11 }}>Search is server-backed and keyboard-friendly for large user populations.</Box>
        </Box>

        {selectedUser && <Paper sx={{ mb: 1.5, p: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#071117', border: '1px solid rgba(110,239,255,0.18)' }}><Box sx={{ width: 34, height: 34, display: 'grid', placeItems: 'center', borderRadius: '50%', bgcolor: 'rgba(110,239,255,0.10)', color: '#6eefff', fontWeight: 900 }}>{String(selectedUser.username || '?').slice(0, 2).toUpperCase()}</Box><Box sx={{ flex: 1 }}><Typography sx={{ fontWeight: 800 }}>{selectedUser.username}</Typography><Typography sx={{ fontSize: 10, color: '#78919c' }}>{selectedUser.is_active === false ? 'Disabled account' : 'Active account'} · {Object.keys(preferences).length} customized sections{latestProfileChange ? ` · last change ${new Date(latestProfileChange).toLocaleDateString()}` : ''}</Typography></Box></Paper>}

        <Tabs value={selectedNamespace} onChange={(_, next) => chooseNamespace(next)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
          {EDITABLE_PROFILE_NAMESPACES.map(item => <Tab key={item.namespace} value={item.namespace} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>{item.label}{preferences[item.namespace] && <Box component="span" title="Customized" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ffd166' }} />}</Box>} />)}
        </Tabs>

        {loadingPrefs ? <CircularProgress size={20} /> : (
          <>
            <Paper sx={{ p: 1.2, mb: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, bgcolor: '#09141b', border: '1px solid rgba(110,239,255,0.14)' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}><InputLabel>Apply template</InputLabel><Select label="Apply template" value="" onChange={e => applyProfileTemplate(e.target.value)}><MenuItem value="baseline">Clinical baseline</MenuItem>{selectedNamespace === WORKSPACE_PREF_NAMESPACE && WORKSPACE_PRESETS.map(preset => <MenuItem key={preset.id} value={preset.id}>{preset.label} workstation</MenuItem>)}</Select></FormControl>
              <FormControl size="small" sx={{ minWidth: 210 }}><InputLabel>Copy from user</InputLabel><Select label="Copy from user" value={copySourceUserId} onChange={e => setCopySourceUserId(e.target.value)}>{users.filter(user => String(user.id) !== String(selectedUserId)).map(user => <MenuItem key={user.id} value={String(user.id)}>{safeUserLabel(user)}</MenuItem>)}</Select></FormControl>
              <Button size="small" variant="outlined" disabled={!copySourceUserId || saving} onClick={copyNamespaceFromUser}>Copy into draft</Button>
              <Box sx={{ flex: 1 }} />
              <FormControlLabel control={<Checkbox checked={showDifferences} onChange={e => setShowDifferences(e.target.checked)} />} label={`Highlight differences (${differencePaths.length})`} />
            </Paper>
            {showDifferences && differencePaths.length > 0 && <Box aria-label="Differences from default" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.2 }}>{differencePaths.slice(0, 16).map(path => <Box key={path} sx={{ px: 0.7, py: 0.25, borderRadius: 99, color: '#ffe29a', bgcolor: 'rgba(255,209,102,0.08)', border: '1px solid rgba(255,209,102,0.22)', fontSize: 9, fontFamily: 'monospace' }}>{path}</Box>)}{differencePaths.length > 16 && <Typography sx={{ fontSize: 9, color: '#899ba2' }}>+{differencePaths.length - 16} more</Typography>}</Box>}
            <Paper data-kairos-marker={CODELESS_PROFILE_CONTROLS_MARKER} data-diff-highlight={showDifferences && differencePaths.length ? 'true' : 'false'} sx={{ p: 2, mb: 2, bgcolor: '#071117', border: showDifferences && differencePaths.length ? '1px solid rgba(255,209,102,0.38)' : '1px solid rgba(110,239,255,0.16)', borderLeft: showDifferences && differencePaths.length ? '3px solid #ffd166' : undefined }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.5 }}>Code-less controls</Typography>
              <Typography sx={{ color: '#86a4af', fontSize: 12, mb: 1.5 }}>
                PACS admins can use these controls without editing code. The JSON panel stays available below for copy/paste and extraction.
              </Typography>
              {renderCodeLessEditor()}
            </Paper>

            <Accordion expanded={jsonExpanded} onChange={(_, expanded) => setJsonExpanded(expanded)} sx={{ bgcolor: '#091016', color: '#d7fbff', border: '1px solid rgba(110,239,255,0.14)' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#9fdce8' }} />}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Advanced JSON copy/export</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Button startIcon={<ContentCopyIcon />} size="small" onClick={copyJsonToClipboard} sx={{ mb: 1 }}>Copy JSON</Button>
                <TextField
                  label={`${selectedNamespace} JSON`}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  multiline
                  minRows={14}
                  fullWidth
                  inputProps={{ 'aria-label': `${selectedNamespace} JSON editor` }}
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 }, '& .MuiInputBase-root': { color: '#d7fbff' } }}
                />
              </AccordionDetails>
            </Accordion>
          </>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 2, position: 'sticky', bottom: 8, zIndex: 2, p: 1, borderRadius: 1.5, bgcolor: 'rgba(6,14,20,0.96)', border: '1px solid rgba(110,239,255,0.14)' }}>
          <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 10, color: dirty ? '#ffd166' : '#78919c' }}>{dirty ? 'UNSAVED PROFILE CHANGES' : 'PROFILE SECTION SAVED'}</Typography></Box>
          <Button startIcon={<SaveIcon />} variant="contained" disabled={!selectedUserId || saving || !dirty} onClick={save}>Save changes</Button>
          <Button startIcon={<RestartAltIcon />} disabled={!selectedUserId || saving} onClick={reset}>Reset namespace</Button>
        </Box>
      </Paper>
    </Box>
  );
}
